import { useEffect, useRef, useState } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { C } from '../MapSharedData';

interface UseUnifiedMapOptions {
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Quick health-check of a raster tile endpoint — loads a single tile (world
 * tile 0/0/0) with a timeout. Lets us fall back to a free vector basemap if the
 * primary tile provider is unreachable/blocked, instead of showing a blank map.
 */
function probeTiles(url: string, timeoutMs = 2500): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(false), timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });
}

/**
 * Single MapLibre instance shared between the world overview and country
 * drill-downs (REFACTOR_PLAN.md). Created ONCE with world defaults — never
 * re-created when the active country changes. Drill-in adjusts viewport /
 * minZoom via mapRef directly (added in steps 3+), so layer crossfades can
 * stay on the GPU instead of being broken up by unmount/remount.
 *
 * Style + controls match the legacy useMapLibre / WorldView setup so the
 * visual baseline is identical when USE_UNIFIED is toggled.
 */
export function useUnifiedMap({ containerRef }: UseUnifiedMapOptions) {
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    const init = async () => {
      // Probe the primary (CARTO) endpoint once. If it's down/blocked, fall back
      // to a free vector basemap so users never see a blank map. Chosen at init
      // (before layers are added) so we don't have to re-create layers on a swap.
      const cartoOk = await probeTiles('https://a.basemaps.cartocdn.com/light_all/0/0/0.png');
      if (cancelled || !containerRef.current || mapRef.current) return;

      const map = new MapLibreMap({
        container: containerRef.current,
        style: cartoOk
          ? {
              version: 8,
              sources: {
                osm: {
                  type: 'raster',
                  tiles: ['https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'],
                  tileSize: 256,
                  attribution: '© OpenStreetMap, © CARTO',
                },
              },
              layers: [
                { id: 'bg', type: 'background', paint: { 'background-color': C.surface } },
                {
                  id: 'osm',
                  type: 'raster',
                  source: 'osm',
                  paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
                },
              ],
            }
          : // Fallback: free, no-key vector basemap (attribution baked into the style).
            'https://tiles.openfreemap.org/styles/positron',
        center: [10, 25],
        zoom: 1.5,
        attributionControl: { compact: true },
        minZoom: 1,
        maxZoom: 14,
      });

      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
      map.on('load', () => setReady(true));
      mapRef.current = map;
    };

    init();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mapRef, ready };
}
